import { useState } from 'react'
import { signIn, signUp, confirmSignUp, confirmSignIn } from 'aws-amplify/auth'
import './LoginPage.css'

function LoginPage({ onSignIn }) {
  const [mode, setMode] = useState('login') // 'login', 'signup', 'confirm', 'newpassword'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [confirmationCode, setConfirmationCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  async function handleLogin(e) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await signIn({
        username: email,
        password
      })

      // Check if new password is required
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setMessage('You must change your temporary password')
        setMode('newpassword')
      } else {
        onSignIn()
      }
    } catch (err) {
      setError(err.message || 'Failed to sign in')
    } finally {
      setLoading(false)
    }
  }

  async function handleNewPassword(e) {
    e.preventDefault()
    setError('')
    setMessage('')
    setLoading(true)

    try {
      await confirmSignIn({
        challengeResponse: newPassword
      })
      setMessage('Password changed successfully!')
      onSignIn()
    } catch (err) {
      setError(err.message || 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  async function handleSignUp(e) {
    e.preventDefault()
    setError('')
    setMessage('')
    setLoading(true)

    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: firstName,
            family_name: lastName
          }
        }
      })
      setMessage('Account created! Please check your email for the confirmation code.')
      setMode('confirm')
    } catch (err) {
      setError(err.message || 'Failed to sign up')
    } finally {
      setLoading(false)
    }
  }

  async function handleConfirm(e) {
    e.preventDefault()
    setError('')
    setMessage('')
    setLoading(true)

    try {
      await confirmSignUp({
        username: email,
        confirmationCode
      })
      setMessage('Email confirmed! You can now sign in.')
      setMode('login')
      setConfirmationCode('')
    } catch (err) {
      setError(err.message || 'Failed to confirm account')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>JAGTEK Access Control</h1>
          <p>Management Portal</p>
        </div>

        <div className="login-card">
          {mode === 'login' && (
            <form onSubmit={handleLogin}>
              <h2>Sign In</h2>

              {error && <div className="alert alert-danger">{error}</div>}
              {message && <div className="alert alert-success">{message}</div>}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              {/* Sign Up disabled - users must be invited by administrators */}
              {/* <div className="login-footer">
                <p>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setMode('signup')
                      setError('')
                      setMessage('')
                    }}
                  >
                    Sign Up
                  </button>
                </p>
              </div> */}
            </form>
          )}

          {mode === 'signup' && (
            <form onSubmit={handleSignUp}>
              <h2>Create Account</h2>

              {error && <div className="alert alert-danger">{error}</div>}

              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <small>Min 8 characters, with uppercase, lowercase, number, and special character</small>
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? 'Creating account...' : 'Sign Up'}
              </button>

              <div className="login-footer">
                <p>
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setMode('login')
                      setError('')
                      setMessage('')
                    }}
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </form>
          )}

          {mode === 'newpassword' && (
            <form onSubmit={handleNewPassword}>
              <h2>Change Password</h2>

              {error && <div className="alert alert-danger">{error}</div>}
              {message && <div className="alert alert-success">{message}</div>}

              <p className="confirm-instructions">
                You must change your temporary password before continuing.
              </p>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                />
                <small>Min 8 characters, with uppercase, lowercase, number, and special character</small>
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? 'Changing password...' : 'Change Password'}
              </button>
            </form>
          )}

          {mode === 'confirm' && (
            <form onSubmit={handleConfirm}>
              <h2>Confirm Email</h2>

              {error && <div className="alert alert-danger">{error}</div>}
              {message && <div className="alert alert-success">{message}</div>}

              <p className="confirm-instructions">
                We sent a confirmation code to <strong>{email}</strong>. Please enter it below.
              </p>

              <div className="form-group">
                <label htmlFor="code">Confirmation Code</label>
                <input
                  id="code"
                  type="text"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  required
                  autoFocus
                  placeholder="123456"
                />
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? 'Confirming...' : 'Confirm Email'}
              </button>

              <div className="login-footer">
                <p>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setMode('login')
                      setError('')
                      setMessage('')
                    }}
                  >
                    Back to Sign In
                  </button>
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default LoginPage
